import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FiUser, FiMapPin, FiPhone, FiMail, FiBook, FiBriefcase,
  FiHeart, FiFileText, FiCheckCircle, FiArrowLeft,
  FiAlertCircle, FiCalendar, FiGlobe, FiActivity, FiUpload, FiX, FiEye
} from 'react-icons/fi';
import { PDFDocument } from 'pdf-lib';

const FormularioSeleccion = ({ campaignData, campaignCode, onBack }) => {
  const [formData, setFormData] = useState({
    tipoDocumento: '',
    dni: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    nombreCompleto: '',
    fechaNacimiento: '',
    departamento: '',
    provincia: '',
    distrito: '',
    direccionExacta: '',
    numeroCelular: '9',
    numeroWhatsapp: '9',
    email: '',
    numeroReferenciaEmerg: '9',
    nivelEducacion: '',
    carrera: '',
    cuentaExperiencia: '',
    detalleExperiencia: '',
    canal: '',
    esReingreso: '',
    enProcesoSeleccion: '',
    enCapacitacion: '',
    campaniaPostula: '',
    modalidadGestion: '',
    tieneComputadora: '',
    tipoConexionInternet: '',
    turnoDisponible: '',
    cuentaDiscapacidad: '',
    detalleDiscapacidad: '',
    carnetConadis: '',
    condicionesMedicas: '',
    usaMedicacion: '',
    esGestante: '',
    tratamientoPsicologico: '',
    diagnosticoPsicologico: '',
    cvRuta: '',
    declaro: '',
    codigoRequerimiento: campaignCode,
    
    seleccionIds: null
  });

  const [ubicacionesData, setUbicacionesData] = useState({});
  const [departamentos, setDepartamentos] = useState([]);
  const [provincias, setProvincias] = useState([]);
  const [distritos, setDistritos] = useState([]);
  const [departamentoSeleccionado, setDepartamentoSeleccionado] = useState('');
  const [reclutadores, setReclutadores] = useState([]);
  const [reclutadorSeleccionado, setReclutadorSeleccionado] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const [cvPreview, setCvPreview] = useState(null);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [cvConfirmed, setCvConfirmed] = useState(false);

  useEffect(() => {
    loadUbicaciones();
    
    // Procesar los seleccionIds del campaignData
    if (campaignData?.seleccionIds) {
      const ids = campaignData.seleccionIds.split(',').map(id => id.trim()).filter(id => id);
      setReclutadores(ids);
    }
  }, [campaignData]);

  const loadUbicaciones = async () => {
    try {
      const response = await fetch('/ubigeoPeru.json');
      const data = await response.json();
      setUbicacionesData(data);
      const departamentosList = Object.keys(data);
      setDepartamentos(departamentosList);
    } catch (error) {
      console.error('Error loading ubicaciones:', error);
    }
  };

  // Función para comprimir PDF
  const compressPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      
      // Serializar el PDF con opciones de compresión
      const pdfBytes = await pdfDoc.save({
        useObjectStreams: true,
        addDefaultPage: false,
        objectsPerTick: 50,
      });
      
      // Crear nuevo archivo comprimido
      const compressedBlob = new Blob([pdfBytes], { type: 'application/pdf' });
      
      // Calcular reducción de tamaño
      const originalSize = file.size;
      const compressedSize = compressedBlob.size;
      const reduction = ((originalSize - compressedSize) / originalSize * 100).toFixed(1);
      
      console.log(`PDF comprimido: ${(originalSize / 1024).toFixed(2)} KB → ${(compressedSize / 1024).toFixed(2)} KB (${reduction}% de reducción)`);
      
      return compressedBlob;
    } catch (error) {
      console.error('Error comprimiendo PDF:', error);
      return file; // Si falla la compresión, retornar archivo original
    }
  };

  // Manejar selección de archivo CV
  const handleCvFileChange = async (e) => {
    const file = e.target.files[0];
    
    if (!file) return;

    // Validar que sea PDF
    if (file.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF', {
        position: "top-center",
        autoClose: 3000,
      });
      e.target.value = null;
      return;
    }

    // Validar tamaño máximo (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo no debe superar los 10 MB', {
        position: "top-center",
        autoClose: 3000,
      });
      e.target.value = null;
      return;
    }

    // Validar que se haya ingresado el número de documento
    if (!formData.dni) {
      toast.error('Primero debes ingresar tu número de documento', {
        position: "top-center",
        autoClose: 3000,
      });
      e.target.value = null;
      return;
    }

    setUploadingCv(true);

    try {
      // Comprimir PDF
      const compressedFile = await compressPDF(file);
      
      // Renombrar archivo con el número de documento
      const newFileName = `${formData.dni}.pdf`;
      const renamedFile = new File([compressedFile], newFileName, { type: 'application/pdf' });
      
      setCvFile(renamedFile);
      
      // Crear URL para preview
      const fileUrl = URL.createObjectURL(renamedFile);
      setCvPreview(fileUrl);
      setCvConfirmed(false);
      
      toast.success(`CV cargado correctamente: ${newFileName}`, {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (error) {
      console.error('Error procesando CV:', error);
      toast.error('Error al procesar el archivo', {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setUploadingCv(false);
    }
  };

  // Eliminar CV seleccionado
  const handleRemoveCv = () => {
    setCvFile(null);
    setCvPreview(null);
    setCvConfirmed(false);
    if (cvPreview) {
      URL.revokeObjectURL(cvPreview);
    }
  };

  // Confirmar CV
  const handleConfirmCv = () => {
    setCvConfirmed(true);
    toast.success('CV confirmado correctamente', {
      position: "top-center",
      autoClose: 2000,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Formatear a mayúsculas para campos de texto
    if (['apellidoPaterno', 'apellidoMaterno', 'nombreCompleto', 'provincia', 'distrito', 'direccionExacta', 'carrera', 'detalleExperiencia', 'detalleDiscapacidad', 'diagnosticoPsicologico'].includes(name)) {
      formattedValue = value.toUpperCase();
    }

    // Validar DNI (8 dígitos)
    if (name === 'dni' && formData.tipoDocumento === 'DNI') {
      formattedValue = value.replace(/\D/g, '').substring(0, 8);
    }

    // Validar números de teléfono (prefijo 9 fijo + máximo 8 dígitos adicionales)
    if (['numeroCelular', 'numeroWhatsapp', 'numeroReferenciaEmerg'].includes(name)) {
      // Solo permitir números
      const numbersOnly = value.replace(/\D/g, '');
      
      // Si no empieza con 9, agregarlo
      if (numbersOnly.length === 0) {
        formattedValue = '9';
      } else if (numbersOnly.length === 1 && numbersOnly !== '9') {
        formattedValue = '9' + numbersOnly;
      } else if (numbersOnly.length > 1 && numbersOnly[0] !== '9') {
        formattedValue = '9' + numbersOnly.substring(0, 8);
      } else {
        // Si ya empieza con 9, limitar a 9 dígitos total
        formattedValue = numbersOnly.substring(0, 9);
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Actualizar provincias y distritos cuando cambie el departamento
    if (name === 'departamento') {
      setDepartamentoSeleccionado(value);
      if (value && ubicacionesData[value]) {
        const provinciasList = Object.keys(ubicacionesData[value]);
        setProvincias(provinciasList);
        setDistritos([]);
        setFormData(prev => ({
          ...prev,
          provincia: '',
          distrito: ''
        }));
      } else {
        setProvincias([]);
        setDistritos([]);
      }
    }

    if (name === 'provincia') {
      if (departamentoSeleccionado && value && ubicacionesData[departamentoSeleccionado] && ubicacionesData[departamentoSeleccionado][value]) {
        const distritosList = ubicacionesData[departamentoSeleccionado][value];
        setDistritos(distritosList);
        setFormData(prev => ({
          ...prev,
          distrito: ''
        }));
      } else {
        setDistritos([]);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validación de reclutador si existen opciones
    if (reclutadores.length > 0 && !formData.seleccionIds) {
      newErrors.seleccionIds = 'Por favor selecciona un reclutador';
    }

    // Validaciones obligatorias
    if (!formData.tipoDocumento) newErrors.tipoDocumento = 'Seleccione tipo de documento';
    if (!formData.dni) newErrors.dni = 'Ingrese número de documento';
    if (!formData.apellidoPaterno) newErrors.apellidoPaterno = 'Ingrese apellido paterno';
    if (!formData.apellidoMaterno) newErrors.apellidoMaterno = 'Ingrese apellido materno';
    if (!formData.nombreCompleto) newErrors.nombreCompleto = 'Ingrese nombre completo';
    if (!formData.fechaNacimiento) newErrors.fechaNacimiento = 'Ingrese fecha de nacimiento';
    if (!formData.departamento) newErrors.departamento = 'Seleccione departamento';
    if (!formData.provincia) newErrors.provincia = 'Seleccione provincia';
    if (!formData.distrito) newErrors.distrito = 'Seleccione distrito';
    if (!formData.direccionExacta) newErrors.direccionExacta = 'Ingrese dirección';
    if (!formData.numeroCelular) newErrors.numeroCelular = 'Ingrese número de celular';
    if (!formData.email) newErrors.email = 'Ingrese email';
    if (!formData.nivelEducacion) newErrors.nivelEducacion = 'Seleccione nivel de educación';
    if (!formData.cuentaExperiencia) newErrors.cuentaExperiencia = 'Seleccione si cuenta con experiencia';
    if (!formData.canal) newErrors.canal = 'Seleccione canal de contacto';
    if (!formData.esReingreso) newErrors.esReingreso = 'Seleccione si es reingreso';
    if (!formData.enProcesoSeleccion) newErrors.enProcesoSeleccion = 'Seleccione si está en proceso de selección';
    if (!formData.enCapacitacion) newErrors.enCapacitacion = 'Seleccione si está en capacitación';
    if (!formData.modalidadGestion) newErrors.modalidadGestion = 'Seleccione modalidad de gestión';
    if (!formData.tieneComputadora) newErrors.tieneComputadora = 'Seleccione si tiene computadora';
    if (!formData.tipoConexionInternet) newErrors.tipoConexionInternet = 'Seleccione tipo de conexión a internet';
    if (!formData.turnoDisponible) newErrors.turnoDisponible = 'Seleccione turno disponible';
    if (!formData.cuentaDiscapacidad) newErrors.cuentaDiscapacidad = 'Seleccione si cuenta con discapacidad';
    if (!formData.condicionesMedicas) newErrors.condicionesMedicas = 'Seleccione condiciones médicas';
    if (!formData.esGestante) newErrors.esGestante = 'Seleccione si es gestante';
    if (!formData.tratamientoPsicologico) newErrors.tratamientoPsicologico = 'Seleccione si tiene tratamiento psicológico';
    if (!formData.declaro) newErrors.declaro = 'Debe declarar veracidad de la información';

    // Validaciones específicas
    if (formData.tipoDocumento === 'DNI' && formData.dni.length !== 8) {
      newErrors.dni = 'DNI debe tener 8 dígitos';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData.nivelEducacion === 'SUPERIOR' && !formData.carrera) {
      newErrors.carrera = 'Ingrese carrera para educación superior';
    }

    if (formData.cuentaExperiencia === 'SI' && !formData.detalleExperiencia) {
      newErrors.detalleExperiencia = 'Ingrese detalle de experiencia';
    }

    if (formData.cuentaDiscapacidad === 'SI') {
      if (!formData.detalleDiscapacidad) newErrors.detalleDiscapacidad = 'Ingrese detalle de discapacidad';
      if (!formData.carnetConadis) newErrors.carnetConadis = 'Seleccione si cuenta con carnet CONADIS';
    }

    if (formData.condicionesMedicas === 'SI' && !formData.usaMedicacion) {
      newErrors.usaMedicacion = 'Seleccione si usa medicación';
    }

    if (formData.tratamientoPsicologico === 'SI' && !formData.diagnosticoPsicologico) {
      newErrors.diagnosticoPsicologico = 'Ingrese diagnóstico psicológico';
    }

    // Validación del CV
    if (!cvFile) {
      newErrors.cvFile = 'Debe cargar su CV en formato PDF';
    } else if (!cvConfirmed) {
      newErrors.cvFile = 'Debe confirmar que el CV es correcto antes de enviar';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSuccess('');

    try {
      // Preparar payload
      const payload = {
        tipoDocumento: formData.tipoDocumento,
        dni: formData.dni,
        apellidoPaterno: formData.apellidoPaterno,
        apellidoMaterno: formData.apellidoMaterno,
        nombreCompleto: formData.nombreCompleto,
        fechaNacimiento: formData.fechaNacimiento || null,
        departamento: formData.departamento,
        provincia: formData.provincia,
        distrito: formData.distrito,
        direccionExacta: formData.direccionExacta,
        numeroCelular: formData.numeroCelular,
        numeroWhatsapp: formData.numeroWhatsapp || formData.numeroCelular,
        email: formData.email,
        numeroReferenciaEmerg: formData.numeroReferenciaEmerg || '',
        nivelEducacion: formData.nivelEducacion,
        carrera: formData.carrera || '',
        cuentaExperiencia: formData.cuentaExperiencia,
        detalleExperiencia: formData.detalleExperiencia || '',
        canal: formData.canal,
        esReingreso: formData.esReingreso,
        enProcesoSeleccion: formData.enProcesoSeleccion,
        enCapacitacion: formData.enCapacitacion,
        campaniaPostula: '',
        modalidadGestion: formData.modalidadGestion,
        tieneComputadora: formData.tieneComputadora,
        tipoConexionInternet: formData.tipoConexionInternet,
        turnoDisponible: formData.turnoDisponible,
        cuentaDiscapacidad: formData.cuentaDiscapacidad,
        detalleDiscapacidad: formData.detalleDiscapacidad || '',
        carnetConadis: formData.carnetConadis || '',
        condicionesMedicas: formData.condicionesMedicas || '',
        usaMedicacion: formData.usaMedicacion,
        esGestante: formData.esGestante,
        tratamientoPsicologico: formData.tratamientoPsicologico,
        diagnosticoPsicologico: formData.diagnosticoPsicologico || '',
        cvRuta: cvFile ? cvFile.name : '',  // Por ahora solo el nombre del archivo
        declaro: formData.declaro,
        codigoRequerimiento: formData.codigoRequerimiento,
        seleccionIds: formData.seleccionIds || null
      };

      // Crear la persona con la API
      const personaResponse = await axios.post(
        'https://postula.doms.pe/api/partner-service/v1/2025/october/applicants',
        payload,
        {
          headers: {
            'api.security.token': 'MiTokenUltraSecretoLouisTech2025'
          }
        }
      );
      const personaId = personaResponse.data.id;

      // No se requiere asignación automática a solicitud
      // La asignación se hará manualmente desde el sistema
      
      toast.success('¡Formulario enviado exitosamente! Su postulación ha sido registrada.', {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Regresar a validación de código después de 2 segundos
      setTimeout(() => {
        onBack();
      }, 2000);

    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Error al enviar el formulario. Por favor intente nuevamente.', {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const renderSelectField = (name, label, options, required = true, icon = null) => (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700 flex items-center">
        {icon && <span className="mr-2">{icon}</span>}
        {label} {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="custom-select">
        <select
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className={`form-select w-full px-4 py-3 rounded-lg ${errors[name] ? 'error' : ''
            }`}
          disabled={loading}
        >
          <option value="">Seleccionar...</option>
          {options.map(option => (
            <option key={option.value || option} value={option.value || option}>
              {option.label || option}
            </option>
          ))}
        </select>
      </div>
      {errors[name] && (
        <div className="flex items-center text-red-500 text-sm">
          <FiAlertCircle className="w-4 h-4 mr-1" />
          {errors[name]}
        </div>
      )}
    </div>
  );

  const renderInputField = (name, label, type = 'text', required = true, placeholder = '', icon = null) => (
    <div className="space-y-2">
      <label className="block text-sm font-semibold text-gray-700 flex items-center">
        {icon && <span className="mr-2">{icon}</span>}
        {label} {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={formData[name]}
        onChange={handleInputChange}
        placeholder={placeholder}
        className={`form-input w-full px-4 py-3 rounded-lg ${errors[name] ? 'error' : ''
          }`}
        disabled={loading}
      />
      {errors[name] && (
        <div className="flex items-center text-red-500 text-sm">
          <FiAlertCircle className="w-4 h-4 mr-1" />
          {errors[name]}
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full max-w-6xl mx-auto">
      <div className="form-container rounded-2xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-24 h-24 mb-6 flex items-center justify-center">
            <img
              src="/logopartner.png"
              alt="Logo"
              className="w-full h-full object-contain rounded-full shadow-sm"
            />
          </div>

          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            Formulario de Postulación
          </h1>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 text-lg">
            <div className="flex items-center space-x-2">
              <FiGlobe className="w-5 h-5 text-verde-esmeralda" />
              <span className="text-gray-600">Código:</span>
              <span className="font-semibold text-verde-esmeralda">{formData.codigoRequerimiento}</span>
            </div>
          </div>
        </div>

        {success && (
          <div className="flex items-center bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-6">
            <FiCheckCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="text-sm font-medium">{success}</span>
          </div>
        )}

        {errors.submit && (
          <div className="flex items-center bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6">
            <FiAlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
            <span className="text-sm font-medium">{errors.submit}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Selección de Reclutador */}
          {reclutadores.length > 0 && (
            <div className="form-section rounded-xl p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200">
              <div className="section-header rounded-lg px-4 py-3 mb-6 bg-white shadow-sm">
                <h2 className="text-xl font-bold flex items-center text-white-800">
                  <FiUser className="w-5 h-5 mr-3" />
                  ¿Quién es tu reclutador?
                </h2>
                <p className="text-sm text-white-600 mt-2">
                  Selecciona al reclutador que te contactó para esta postulación
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {reclutadores.map((reclutador) => (
                  <div
                    key={reclutador}
                    onClick={() => {
                      setReclutadorSeleccionado(reclutador);
                      setFormData({ ...formData, seleccionIds: reclutador });
                    }}
                    className={`relative cursor-pointer rounded-xl p-4 border-2 transition-all duration-300 transform hover:scale-105 ${
                      reclutadorSeleccionado === reclutador
                        ? 'border-blue-600 bg-blue-100 shadow-lg'
                        : 'border-gray-300 bg-white hover:border-blue-400 hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          reclutadorSeleccionado === reclutador
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-600'
                        }`}>
                          <FiUser className="w-5 h-5" />
                        </div>
                        <div>
                          <p className={`font-semibold ${
                            reclutadorSeleccionado === reclutador
                              ? 'text-blue-800'
                              : 'text-gray-800'
                          }`}>
                            {reclutador}
                          </p>
                          <p className="text-xs text-gray-500">Reclutador</p>
                        </div>
                      </div>
                      {reclutadorSeleccionado === reclutador && (
                        <FiCheckCircle className="w-6 h-6 text-blue-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {errors.seleccionIds && (
                <div className="mt-4 text-red-600 text-sm flex items-center">
                  <FiAlertCircle className="w-4 h-4 mr-2" />
                  {errors.seleccionIds}
                </div>
              )}
            </div>
          )}

          {/* Datos Personales */}
          <div className="form-section rounded-xl p-6">
            <div className="section-header rounded-lg px-4 py-3 mb-6">
              <h2 className="text-xl font-bold flex items-center">
                <FiUser className="w-5 h-5 mr-3" />
                Datos Personales
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {renderSelectField('tipoDocumento', 'Tipo de Documento', [
                { value: 'DNI', label: 'DNI' },
                { value: 'CARNET DE EXTRANJERIA', label: 'Carnet de Extranjería' },
                { value: 'PASAPORTE', label: 'Pasaporte' }
              ], true, <FiUser />)}

              {renderInputField('dni', 'Número de Documento', 'text', true,
                formData.tipoDocumento === 'DNI' ? '12345678' : 'Número de documento', <FiUser />)}

              {renderInputField('apellidoPaterno', 'Apellido Paterno', 'text', true, 'Apellido paterno', <FiUser />)}

              {renderInputField('apellidoMaterno', 'Apellido Materno', 'text', true, 'Apellido materno', <FiUser />)}

              {renderInputField('nombreCompleto', 'Nombres', 'text', true, 'Nombres completos', <FiUser />)}

              {renderInputField('fechaNacimiento', 'Fecha de Nacimiento', 'date', true, '', <FiCalendar />)}
            </div>
          </div>

          {/* Ubicación */}
          <div className="form-section rounded-xl p-6">
            <div className="section-header rounded-lg px-4 py-3 mb-6">
              <h2 className="text-xl font-bold flex items-center">
                <FiMapPin className="w-5 h-5 mr-3" />
                Ubicación
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              {renderSelectField('departamento', 'Departamento', departamentos.map(d => ({ value: d, label: d })), true, <FiMapPin />)}

              {renderSelectField('provincia', 'Provincia', provincias.map(p => ({ value: p, label: p })), true, <FiMapPin />)}

              {renderSelectField('distrito', 'Distrito', distritos.map(d => ({ value: d, label: d })), true, <FiMapPin />)}
            </div>

            <div>
              {renderInputField('direccionExacta', 'Dirección Exacta', 'text', true, 'Calle, número, referencia', <FiMapPin />)}
            </div>
          </div>

          {/* Contacto */}
          <div className="form-section rounded-xl p-6">
            <div className="section-header rounded-lg px-4 py-3 mb-6">
              <h2 className="text-xl font-bold flex items-center">
                <FiPhone className="w-5 h-5 mr-3" />
                Información de Contacto
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {renderInputField('numeroCelular', 'Número de Celular', 'tel', true, '912345678', <FiPhone />)}
              
              {renderInputField('numeroWhatsapp', 'Número de WhatsApp', 'tel', false, '912345678', <FiPhone />)}
              
              {renderInputField('email', 'Email', 'email', true, 'correo@ejemplo.com', <FiMail />)}
              
              {renderInputField('numeroReferenciaEmerg', 'Número de Referencia de Emergencia', 'tel', false, '912345678', <FiPhone />)}
            </div>
          </div>

          {/* Educación y Experiencia */}
          <div className="form-section rounded-xl p-6">
            <div className="section-header rounded-lg px-4 py-3 mb-6">
              <h2 className="text-xl font-bold flex items-center">
                <FiBook className="w-5 h-5 mr-3" />
                Educación y Experiencia
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {renderSelectField('nivelEducacion', 'Nivel de Educación', [
                { value: 'BASICA', label: 'Básica' },
                { value: 'SUPERIOR', label: 'Superior' }
              ], true, <FiBook />)}

              {formData.nivelEducacion === 'SUPERIOR' &&
                renderInputField('carrera', 'Carrera Profesional', 'text', true, 'Nombre de la carrera', <FiBook />)}

              {renderSelectField('cuentaExperiencia', '¿Cuenta con Experiencia?', [
                { value: 'SI', label: 'Sí' },
                { value: 'NO', label: 'No' }
              ], true, <FiBriefcase />)}

              {formData.cuentaExperiencia === 'SI' &&
                renderInputField('detalleExperiencia', 'Detalle de Experiencia', 'text', true, 'Describa su experiencia laboral', <FiBriefcase />)}

              {renderSelectField('canal', '¿Cómo nos conociste?', [
                { value: 'FACEBOOK', label: 'Facebook' },
                { value: 'INSTAGRAM', label: 'Instagram' },
                { value: 'COMPUTRABAJO', label: 'Computrabajo' },
                { value: 'TIKTOK', label: 'TikTok' },
                { value: 'UNIVERSIDAD', label: 'Universidad' },
                { value: 'INDEED', label: 'Indeed' },
                { value: 'BUMERAN', label: 'Bumeran' },
                { value: 'MUNICIPALIDAD', label: 'Municipalidad' },
                { value: 'FERIALABORAL', label: 'Feria Laboral' },
                { value: 'REFERIDO', label: 'Referido' }
              ], true, <FiGlobe />)}
            </div>
          </div>

          {/* Estado Laboral */}
          <div className="form-section rounded-xl p-6">
            <div className="section-header rounded-lg px-4 py-3 mb-6">
              <h2 className="text-xl font-bold flex items-center">
                <FiActivity className="w-5 h-5 mr-3" />
                Estado Laboral
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {renderSelectField('esReingreso', '¿Es Reingreso?', [
                { value: 'SI', label: 'Sí' },
                { value: 'NO', label: 'No' }
              ])}

              {renderSelectField('enProcesoSeleccion', '¿Está en Proceso de Selección?', [
                { value: 'SI', label: 'Sí' },
                { value: 'NO', label: 'No' }
              ])}

              {renderSelectField('enCapacitacion', '¿Está en Capacitación?', [
                { value: 'SI', label: 'Sí' },
                { value: 'NO', label: 'No' }
              ])}
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderSelectField('modalidadGestion', 'Modalidad de Gestión', [
                { value: 'PRESENCIAL', label: 'Presencial' },
                { value: 'REMOTO', label: 'Remoto' },
                { value: 'HIBRIDO', label: 'Híbrido' }
              ])}

              {renderSelectField('turnoDisponible', 'Turno Disponible', [
                { value: 'MAÑANA', label: 'Mañana' },
                { value: 'TARDE', label: 'Tarde' },
                { value: 'NOCHE', label: 'Noche' },
                { value: 'MADRUGADA', label: 'Madrugada' },
                { value: 'FULLTIME', label: 'Full Time' },
                { value: 'PARTTIME', label: 'Part Time' }
              ])}

              {renderSelectField('tieneComputadora', '¿Tiene Computadora?', [
                { value: 'SI', label: 'Sí' },
                { value: 'NO', label: 'No' }
              ])}

              {renderSelectField('tipoConexionInternet', 'Tipo de Conexión a Internet', [
                { value: 'CABLEADO', label: 'Cableado' },
                { value: 'WIFI', label: 'WiFi' },
                { value: 'NINGUNO', label: 'Ninguno' }
              ])}
            </div>
          </div>

          {/* Salud y Discapacidad */}
          <div className="bg-white bg-opacity-50 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b border-gray-200 pb-2">
              Información de Salud
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderSelectField('cuentaDiscapacidad', '¿Cuenta con Discapacidad?', [
                { value: 'SI', label: 'Sí' },
                { value: 'NO', label: 'No' }
              ])}

              {formData.cuentaDiscapacidad === 'SI' &&
                renderInputField('detalleDiscapacidad', 'Detalle de Discapacidad', 'text', true)}

              {formData.cuentaDiscapacidad === 'SI' &&
                renderSelectField('carnetConadis', '¿Cuenta con Carnet CONADIS?', [
                  { value: 'SI', label: 'Sí' },
                  { value: 'NO', label: 'No' }
                ])}

              {renderSelectField('condicionesMedicas', '¿Tiene Condiciones Médicas?', [
                { value: 'SI', label: 'Sí' },
                { value: 'NO', label: 'No' }
              ])}

              {formData.condicionesMedicas === 'SI' &&
                renderSelectField('usaMedicacion', '¿Usa Medicación?', [
                  { value: 'SI', label: 'Sí' },
                  { value: 'NO', label: 'No' }
                ])}

              {renderSelectField('esGestante', '¿Es Gestante?', [
                { value: 'SI', label: 'Sí' },
                { value: 'NO', label: 'No' }
              ])}

              {renderSelectField('tratamientoPsicologico', '¿Recibe Tratamiento Psicológico?', [
                { value: 'SI', label: 'Sí' },
                { value: 'NO', label: 'No' }
              ])}

              {formData.tratamientoPsicologico === 'SI' &&
                renderInputField('diagnosticoPsicologico', 'Diagnóstico Psicológico', 'text', true)}
            </div>
          </div>

          {/* Documentos y Declaración */}
          <div className="bg-white bg-opacity-50 rounded-2xl p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 border-b border-gray-200 pb-2 flex items-center">
              <FiFileText className="w-5 h-5 mr-2" />
              Documentos y Declaración
            </h2>

            <div className="space-y-6">
              {/* Sección de carga de CV */}
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Curriculum Vitae (PDF) <span className="text-red-500">*</span>
                </label>
                
                {!cvFile ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-500 transition-colors">
                    <label className="cursor-pointer">
                      <div className="flex flex-col items-center space-y-3">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                          <FiUpload className="w-8 h-8 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-700">
                            {uploadingCv ? 'Procesando...' : 'Haz clic para cargar tu CV'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Solo archivos PDF (máx. 10MB)
                          </p>
                          {formData.dni && (
                            <p className="text-xs text-blue-600 mt-2 font-medium">
                              Se guardará como: {formData.dni}.pdf
                            </p>
                          )}
                        </div>
                      </div>
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={handleCvFileChange}
                        className="hidden"
                        disabled={loading || uploadingCv || !formData.dni}
                      />
                    </label>
                  </div>
                ) : (
                  <div className="border-2 border-green-500 rounded-xl p-4 bg-green-50">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                          <FiFileText className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-800">{cvFile.name}</p>
                          <p className="text-xs text-gray-500">
                            {(cvFile.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveCv}
                        className="p-2 hover:bg-red-100 rounded-full transition-colors"
                        disabled={loading}
                      >
                        <FiX className="w-5 h-5 text-red-600" />
                      </button>
                    </div>

                    {/* Preview y confirmación */}
                    <div className="space-y-3">
                      <div className="flex items-center justify-between bg-white rounded-lg p-3">
                        <span className="text-sm font-medium text-gray-700">
                          Previsualizar documento
                        </span>
                        <a
                          href={cvPreview}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          <FiEye className="w-4 h-4" />
                          <span>Ver PDF</span>
                        </a>
                      </div>

                      {!cvConfirmed ? (
                        <button
                          type="button"
                          onClick={handleConfirmCv}
                          className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold flex items-center justify-center space-x-2"
                          disabled={loading}
                        >
                          <FiCheckCircle className="w-5 h-5" />
                          <span>Confirmar que el CV es correcto</span>
                        </button>
                      ) : (
                        <div className="flex items-center justify-center space-x-2 py-3 bg-green-600 text-white rounded-lg">
                          <FiCheckCircle className="w-5 h-5" />
                          <span className="font-semibold">CV confirmado ✓</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {errors.cvFile && (
                  <div className="flex items-center text-red-600 text-sm mt-2">
                    <FiAlertCircle className="w-4 h-4 mr-2" />
                    {errors.cvFile}
                  </div>
                )}

                <p className="text-xs text-gray-600 mt-2">
                  💡 <strong>Importante:</strong> El archivo será comprimido automáticamente y guardado con tu número de documento para facilitar su identificación.
                </p>
              </div>

              {/* Declaración */}
              <div className="space-y-2 pt-4 border-t border-gray-200">
                <label className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    name="declaro"
                    checked={formData.declaro === 'SI'}
                    onChange={(e) => setFormData(prev => ({ ...prev, declaro: e.target.checked ? 'SI' : '' }))}
                    className="mt-1"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-700">
                    Declaro bajo juramento que toda la información proporcionada es veraz y autorizo
                    la verificación de los datos consignados. <span className="text-red-500">*</span>
                  </span>
                </label>
                {errors.declaro && (
                  <div className="flex items-center text-red-600 text-sm">
                    <FiAlertCircle className="w-4 h-4 mr-2" />
                    {errors.declaro}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-4 justify-between pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onBack}
              className="btn-secondary px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center"
              disabled={loading}
            >
              <FiArrowLeft className="w-5 h-5 mr-2" />
              Volver
            </button>

            <button
              type="submit"
              disabled={loading}
              className={`px-8 py-4 rounded-xl font-semibold text-white text-lg flex items-center justify-center transition-all duration-200 ${loading
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'btn-primary hover:shadow-lg'
                }`}
            >
              {loading ? (
                <>
                  <div className="loading-spinner w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-3"></div>
                  Enviando...
                </>
              ) : (
                <>
                  <FiCheckCircle className="w-5 h-5 mr-2" />
                  Enviar Formulario
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormularioSeleccion;
